import React, { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useReviews } from "../components/context/ReviewContext";
import { CDN_URL } from "./config/apiconfig";

const StarRating = ({ rating, maxStars = 5 }) => {
  return (
    <div
      className="flex items-center gap-1"
      role="img"
      aria-label={`${rating} out of ${maxStars} stars`}
    >
      {Array.from({ length: maxStars }).map((_, index) => (
        <Star
          key={index}
          className={`w-4 h-4 ${
            index < rating
              ? "fill-yellow-400 text-yellow-400"
              : "fill-gray-200 text-gray-200"
          }`}
          aria-hidden="true"
        />
      ))}
      <span className="sr-only">
        {rating} out of {maxStars} stars
      </span>
    </div>
  );
};

const TestimonialText = ({ text, testimonialId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);

  useEffect(() => {
    // Check if text is longer than approximately 3 lines (rough estimation)
    const words = text.split(" ");
    setShowReadMore(words.length > 20 || text.length > 150);
  }, [text]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const truncatedText =
    text.length > 150 ? text.substring(0, 150) + "..." : text;

  return (
    <div className="text-sm sm:text-base md:text-lg text-zinc-800 flex-grow flex flex-col justify-center overflow-hidden">
      <blockquote className="break-words overflow-wrap-anywhere hyphens-auto">
        <span
          className={`${!isExpanded && showReadMore ? "line-clamp-3" : ""}`}
        >
          "{isExpanded || !showReadMore ? text : truncatedText}"
        </span>
      </blockquote>
      {/* {showReadMore && (
        <button
          onClick={toggleExpanded}
          className="text-teal-600 hover:text-teal-700 text-xs mt-2 font-medium transition-colors duration-200 self-start"
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )} */}
    </div>
  );
};

const Testimonials = () => {
  const { reviews, loading, error } = useReviews();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonialsPerView, setTestimonialsPerView] = useState(3);

  /* ---------------- RESPONSIVE ITEMS ---------------- */
  useEffect(() => {
    const updateTestimonialsPerView = () => {
      if (window.innerWidth < 768) {
        setTestimonialsPerView(1); // Mobile: 1 testimonial
      } else if (window.innerWidth < 1024) {
        setTestimonialsPerView(2); // Tablet: 2 testimonials
      } else if (window.innerWidth < 1280) {
        setTestimonialsPerView(3); // Small desktop: 3 testimonials
      } else {
        setTestimonialsPerView(3); // Large desktop: 3 testimonials
      }
    };

    updateTestimonialsPerView();
    window.addEventListener("resize", updateTestimonialsPerView);
    return () =>
      window.removeEventListener("resize", updateTestimonialsPerView);
  }, []);

  /* ---------------- MAP API → UI ---------------- */
  const testimonials = useMemo(() => {
    return reviews
      .filter((r) => r.status === "Active")
      .map((r, index) => ({
        id: r.id,
        avatar: r.image
          ? `${CDN_URL}/${r.image}`
          : "https://cdn.acsdev.in/ManaPrint/1773641688_download%20(1).jpg",
        text: r.text,
        name: r.userName ? r.userName : `User ${index + 1}`,
        rating: r.rating,
      }));
  }, [reviews]);

  const maxSlides = Math.max(0, testimonials.length - testimonialsPerView);
  const needsNavigation = testimonials.length > testimonialsPerView;
  const hasTestimonials = testimonials.length > 0;

  const nextSlide = () => {
    setCurrentSlide((prev) => Math.min(prev + 1, maxSlides));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  };

  // Calculate the transform offset for sliding animation
  const getTransformOffset = () => {
    const cardWidth = 100 / testimonialsPerView; // Percentage width per card
    return -(currentSlide * cardWidth);
  };

  // Get responsive card width class based on testimonials per view
  const getCardWidthClass = () => {
    switch (testimonialsPerView) {
      case 1:
        return "w-full flex-shrink-0"; // Mobile: full width
      case 2:
        return "w-1/2 flex-shrink-0 px-2"; // Tablet: half width
      case 3:
      default:
        return "w-1/3 flex-shrink-0 px-2"; // Desktop: third width
    }
  };

  /* ---------------- LOADING / ERROR ---------------- */
  if (loading) {
    return (
      <section
        className="overflow-hidden px-32 py-3 mt-6 w-full max-lg:px-10 max-md:px-5 max-md:py-4"
        aria-label="Customer testimonials"
      >
        <div
          className="text-center py-10 text-gray-600"
          role="status"
          aria-live="polite"
        >
          <div className="inline-flex items-center gap-2">
            <div
              className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"
              aria-hidden="true"
            ></div>
            Loading testimonials...
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="overflow-hidden px-32 py-3 mt-6 w-full max-lg:px-10 max-md:px-5 max-md:py-4"
        aria-label="Customer testimonials"
      >
        <div
          className="text-center py-10 text-red-500"
          role="status"
          aria-live="polite"
        >
          {error}
        </div>
      </section>
    );
  }

  if (!hasTestimonials) {
    return (
      <section
        className="overflow-hidden px-32 py-3 mt-6 w-full max-lg:px-10 max-md:px-5 max-md:py-4"
        aria-label="Customer testimonials"
      >
        <div
          className="text-center py-10 text-gray-500"
          role="status"
          aria-live="polite"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="text-6xl opacity-20" aria-hidden="true">
              💬
            </div>
            <div>
              <h3 className="text-lg font-medium mb-2">No testimonials yet</h3>
              <p className="text-sm">Be the first to share your experience</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <section
      className="overflow-hidden px-32 py-3 mt-6 w-full max-lg:px-10 max-md:px-5 max-md:py-4"
      aria-label="Customer testimonials"
    >
      {/* HEADER */}
      <div className="flex flex-col items-center mb-8">
        <h2
          className="text-4xl md:text-5xl lg:text-6xl font-semibold text-center max-md:text-4xl mb-4"
          style={{ fontFamily: "Gloock, serif" }}
        >
          What Our Customers Say
        </h2>
        <p className="text-xl md:text-2xl text-gray-500 text-center max-w-2xl">
          See how our prints brighten lives, one product at a time
        </p>
      </div>

      {/* TESTIMONIALS CAROUSEL */}
      <div className="relative mt-6 w-full max-w-7xl mx-auto">
        {/* Testimonials Carousel */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Navigation Arrow Left - Only show if navigation is needed */}
          {needsNavigation && (
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex justify-center items-center text-2xl md:text-3xl font-black text-center bg-white bg-opacity-80 hover:bg-opacity-100 shadow  text-gray-800 min-h-8 md:min-h-10 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex-shrink-0 z-10"
              aria-label="Previous testimonials"
            >
              <ChevronLeft />
            </button>
          )}

          {/* Testimonials Container */}
          <div className="flex-1 overflow-hidden min-h-[280px] sm:min-h-[330px] md:min-h-[350px] lg:min-h-[390px]">
            <div className="relative w-full h-full">
              {/* Sliding Container */}
              <div
                className="flex transition-transform duration-500 ease-in-out w-full h-full"
                style={{
                  transform: `translateX(${getTransformOffset()}%)`,
                }}
              >
                {testimonials.map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className={`${getCardWidthClass()}`}
                  >
                    <article className="flex flex-col items-center transition-transform duration-300 w-full max-w-xs mx-auto">
                      <div className="group hover:bg-white rounded-[48px] justify-center items-center border-[6px] border-zinc-800 hover:border-teal-700 p-6 sm:p-8 md:px-8 md:py-6 min-h-[260px] sm:min-h-[300px] md:min-h-[330px] lg:min-h-[350px] w-full flex flex-col">
                        <div className="flex flex-col items-center text-center h-full">
                          <img
                            src={testimonial.avatar}
                            alt={`${testimonial.name} avatar`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-[120px] lg:h-[120px] border-2 border-zinc-800 group-hover:border-teal-700 rounded-full object-cover flex-shrink-0"
                            onError={(e) => {
                              e.target.src =
                                "https://cdn.acsdev.in/ManaPrint/1773641688_download%20(1).jpg";
                            }}
                          />

                          <div className="mt-4 sm:mt-4 flex-grow flex flex-col justify-center w-full px-2">
                            <TestimonialText
                              text={testimonial.text}
                              testimonialId={testimonial.id}
                            />
                          </div>

                          <div className="mt-4 sm:mt-2 flex-shrink-0">
                            <div className="font-bold text-sm sm:text-base md:text-lg text-zinc-800 break-words">
                              {testimonial.name}
                            </div>
                            <div className="mt-2 flex justify-center">
                              <StarRating rating={testimonial.rating} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Arrow Right - Only show if navigation is needed */}
          {needsNavigation && (
            <button
              onClick={nextSlide}
              disabled={currentSlide >= maxSlides}
              className="flex justify-center items-center text-2xl md:text-3xl font-black text-center bg-white bg-opacity-80 hover:bg-opacity-100 shadow  text-gray-800 min-h-8 md:min-h-10 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex-shrink-0 z-10"
              aria-label="Next testimonials"
            >
              <ChevronRight />
            </button>
          )}
        </div>

        {/* Carousel Indicators - Only show if navigation is needed */}
        {hasTestimonials && needsNavigation && maxSlides > 0 && (
          <div
            className="flex justify-center mt-1 space-x-2"
            role="tablist"
            aria-label="Carousel navigation"
          >
            {Array.from({ length: maxSlides + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  index === currentSlide
                    ? "bg-teal-600 scale-125"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
                role="tab"
                aria-selected={index === currentSlide}
              />
            ))}
          </div>
        )}

        {/* Mobile-specific: Show total count - Only if there are testimonials */}
        {/* {hasTestimonials && (
          <div className="flex justify-center mt-4 md:hidden">
            <span className="text-sm text-gray-500">
              {needsNavigation
                ? `${currentSlide + 1} - ${Math.min(currentSlide + testimonialsPerView, testimonials.length)} of ${testimonials.length} testimonials`
                : `${testimonials.length} testimonial${testimonials.length !== 1 ? "s" : ""}`}
            </span>
          </div>
        )} */}
      </div>
    </section>
  );
};

export default Testimonials;
